/**
 * T-233 (07.10.2026: „Telefonat von KMVS kann nicht angenommen werden") —
 * der Klick auf „Annehmen", ohne React und ohne SDK testbar.
 *
 * Bis hierhin war der Klick ein stilles `void softphone.answer()`: Fehlte das
 * SDK-Telefon oder die Id des klingelnden Anrufs, passierte NICHTS, und auch
 * ein Fehler des SDK verschwand. Im Ticket stand danach nur „nicht
 * angenommen" — ob überhaupt geklickt wurde, war nicht zu sagen.
 *
 * Jetzt hat jeder Klick ein benanntes Ergebnis. Es landet in der
 * Softphone-Historie des Tickets, in den [telephony]-Spuren und — wenn es
 * nicht geklappt hat — als sichtbarer Hinweis beim Nutzer.
 */

export type AnnehmenErgebnis = { ok: true } | { ok: false; grund: string };

/** Nach so vielen ms ohne `accepted`/`confirmed` gilt die Annahme als unbestätigt. */
export const ANNAHME_BESTAETIGUNG_MS = 3000;

export async function fuehreAnnehmenAus(ctx: {
    phone: { answer(callId: string): unknown } | null;
    callId: string | null;
    sdkStatus: string;
}): Promise<AnnehmenErgebnis> {
    if (!ctx.phone) {
        return { ok: false, grund: `Softphone nicht verbunden (Status ${ctx.sdkStatus || "unbekannt"})` };
    }
    if (!ctx.callId) return { ok: false, grund: "kein klingelnder Anruf mehr (schon beendet?)" };
    try {
        await ctx.phone.answer(ctx.callId);
        return { ok: true };
    } catch (err: unknown) {
        // Das SDK weist mit einem Ergebnis-Objekt `{ code, message }` ab (etwa
        // NOT_FOUND_CALL_ID / -211), nicht mit einem Error.
        const e = err as { name?: string; message?: string; code?: unknown } | null;
        const code = typeof e?.code === "number" ? ` (${e.code})` : "";
        const text = e?.message
            ? `${e.name ? `${e.name}: ` : ""}${e.message}${code}`
            : String(err ?? "unbekannt");
        return { ok: false, grund: `Fehler des Softphones: ${text}`.slice(0, 200) };
    }
}

/**
 * Der Grund, mit dem das SDK einen Anruf beendet: `{ cause, code }` aus
 * `ended`/`failed`/`clientError` (JsSIP-Ursachen wie „Canceled", „Rejected",
 * „Terminated"). `null`, wenn das Ereignis nichts mitgibt.
 */
export function endeGrundAus(ereignis: string, payload: unknown): string | null {
    const p = payload as { cause?: unknown; code?: unknown } | null | undefined;
    const cause = typeof p?.cause === "string" && p.cause.trim() ? p.cause.trim() : null;
    if (!cause) return null;
    const code = typeof p?.code === "number" || typeof p?.code === "string" ? ` (${p.code})` : "";
    return `${ereignis}: ${cause}${code}`.slice(0, 120);
}

/**
 * Lohnt die Fehlermeldung an den Nutzer? Nur, wenn DIESER Anruf nach kurzer
 * Frist immer noch unbeantwortet klingelt — dann muss er handeln.
 *
 * Nicht, wenn das Gespräch trotzdem zustande kam (ein zweiter Klick auf
 * „Annehmen", während das SDK den ersten noch verarbeitete, wird abgewiesen —
 * das Gespräch läuft aber; so am 07.10.2026 bei ic@ gesehen), und nicht, wenn
 * der Anruf schon vorbei ist (der Anrufer hat aufgelegt — da gibt es nichts
 * mehr anzunehmen).
 */
export function hinweisNoetig(
    jetzt: { id: string; state: string; answeredAt: number | null } | null,
    callId: string | null,
): boolean {
    if (!jetzt || !callId || jetzt.id !== callId) return false;
    return jetzt.answeredAt === null && jetzt.state === "ringing";
}

/** Der Text für die Historie: „angenommen" oder der Grund des Scheiterns. */
export function ergebnisText(e: AnnehmenErgebnis): string {
    return e.ok ? "angenommen" : e.grund;
}
